import React from "react";
import { Pencil, Trash2, CheckCircle2, Circle } from "lucide-react";

const TIME_LABEL = {
  morning: "Morning",
  noon: "Noon",
  evening: "Evening",
  bedtime: "Bedtime",
};

export default function MedicationRow({ m, largeText, onEdit, onDelete }) {
  return (
    <div
      data-testid={`med-row-${m.id}`}
      className="bg-white border border-[#E5E2DC] rounded-2xl p-5 print:break-inside-avoid"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[220px]">
          <div className={`med-row-name font-semibold text-[#1C2B23] ${largeText ? "text-3xl" : "text-xl"}`}>
            {m.generic_name}
            {m.brand_name && (
              <span className={`ml-2 text-[#5C6B62] font-normal ${largeText ? "text-xl" : "text-sm"}`}>
                ({m.brand_name})
              </span>
            )}
          </div>
          <div className={`med-row-strength text-[#2B5339] font-semibold mt-1 ${largeText ? "text-2xl" : "text-lg"}`}>
            {m.strength}
          </div>
          {m.purpose && (
            <div className={`text-[#5C6B62] mt-1 ${largeText ? "text-lg" : "text-sm"}`}>
              For: {m.purpose}
            </div>
          )}
          {m.directions && (
            <div className={`text-[#1C2B23] mt-3 leading-relaxed ${largeText ? "text-lg" : "text-base"}`}>
              {m.directions}
            </div>
          )}
        </div>

        {/* Time badges */}
        <div className="flex flex-wrap items-start gap-1.5">
          {m.photo && (
            <img
              data-testid="med-row-photo"
              src={m.photo}
              alt="Reference: labeled container"
              className="w-12 h-12 object-cover rounded border border-[#E5E2DC] mr-1"
            />
          )}
          {(m.times || []).map((t) => (
            <span
              key={t}
              className={`inline-flex items-center bg-[#F0EFEA] text-[#2B5339] rounded-full px-3 py-1 font-medium ${largeText ? "text-base" : "text-xs"}`}
            >
              {TIME_LABEL[t] || t}
            </span>
          ))}
        </div>
      </div>

      {/* Detail grid */}
      <div className={`med-row-meta mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3 ${largeText ? "text-base" : "text-xs"} text-[#5C6B62] pt-4 border-t border-[#F0EFEA]`}>
        <Field label="Form" value={m.form} />
        <Field label="Imprint front" value={m.imprint_front} />
        <Field label="Imprint back" value={m.imprint_back} />
        <Field label="Manufacturer" value={m.manufacturer} />
        <Field label="Pharmacy" value={m.pharmacy} />
        {m.do_not_confuse && (
          <div className="col-span-2 sm:col-span-3">
            <div className="text-[10px] uppercase tracking-wider text-[#C26747] font-semibold">
              Do not confuse with
            </div>
            <div className={`text-[#1C2B23] ${largeText ? "text-lg" : "text-sm"}`}>
              {m.do_not_confuse}
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <span className={`inline-flex items-center gap-1.5 ${largeText ? "text-base" : "text-xs"} ${m.label_reviewed ? "text-[#2B5339]" : "text-[#C26747]"}`}>
          {m.label_reviewed ? (
            <><CheckCircle2 size={14} /> Prescription label reviewed</>
          ) : (
            <><Circle size={14} /> Prescription label not yet reviewed</>
          )}
        </span>
        {(onEdit || onDelete) && (
          <div className="flex gap-2 print:hidden">
            {onEdit && (
              <button
                data-testid={`edit-med-${m.id}`}
                type="button"
                onClick={onEdit}
                className="inline-flex items-center gap-1 text-xs text-[#5C6B62] hover:text-[#2B5339]"
              >
                <Pencil size={12} /> Edit
              </button>
            )}
            {onDelete && (
              <button
                data-testid={`del-med-${m.id}`}
                type="button"
                onClick={onDelete}
                className="inline-flex items-center gap-1 text-xs text-[#5C6B62] hover:text-[#C26747]"
              >
                <Trash2 size={12} /> Remove
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider font-semibold">{label}</div>
    <div className="text-[#1C2B23]">{value || "—"}</div>
  </div>
);
